import apiClient from "../utils/axiosConfig";
import { getApiUrl, API_ENDPOINTS } from "../Configurations/config";

/**
 * Admin Service - wraps all Admin Panel API calls
 * Base path (dev):   /api/admin
 * Base path (prod):  /api/admin
 */
class AdminService {
  /**
   * POST /api/admin/check-access
   * @param {string} email
   * @returns {Promise<{access_granted:boolean,email:string,message:string}>}
   */
  async checkAccess(email) {
    const payload = { email };
    const url = getApiUrl(API_ENDPOINTS.ADMIN.CHECK_ACCESS);
    const response = await apiClient.post(url, payload);
    return response.data;
  }

  /**
   * GET /api/admin/dashboard/summary
   * @param {Object} options
   * @param {number} [options.days] - 1, 7, or 30 (defaults to 7)
   * @param {string} [options.selectedDate] - YYYY-MM-DD (defaults to today - handled by backend)
   * @param {string} [options.startTime] - HH:MM:SS (defaults to 00:00:00)
   * @param {string} [options.endTime] - HH:MM:SS (defaults to 23:59:59)
   * @returns {Promise<DashboardResponse>}
   */
  async getDashboard({
    days = 7,
    selectedDate,
    startTime = "00:00:00",
    endTime = "23:59:59",
  } = {}) {
    const params = {};

    // Always include days parameter (backend validates allowed values)
    if (days) {
      params.days = days;
    }

    // Include selected_date if provided
    if (selectedDate) {
      params.selected_date = selectedDate;
    }

    // Explicitly include time range so all dashboard calls are time-aware
    if (startTime) {
      params.start_time = startTime;
    }
    if (endTime) {
      params.end_time = endTime;
    }

    const url = getApiUrl(API_ENDPOINTS.ADMIN.DASHBOARD);
    const response = await apiClient.get(url, { params });
    return response.data;
  }

  /**
   * GET /api/admin/dashboard/daily
   * @param {Object} options
   * @param {number} [options.days] - 1, 7, or 30 (defaults to 7)
   * @param {string} [options.selectedDate] - YYYY-MM-DD
   * @param {string} [options.startTime] - HH:MM:SS (defaults to 00:00:00)
   * @param {string} [options.endTime] - HH:MM:SS (defaults to 23:59:59)
   */
  async getDashboardDailyData({
    days = 7,
    selectedDate,
    startTime = "00:00:00",
    endTime = "23:59:59",
  } = {}) {
    const params = {};

    if (days) {
      params.days = days;
    }

    if (selectedDate) {
      params.selected_date = selectedDate;
    }

    if (startTime) {
      params.start_time = startTime;
    }
    if (endTime) {
      params.end_time = endTime;
    }

    const url = getApiUrl(API_ENDPOINTS.ADMIN.DASHBOARDDAILYDATA);
    const response = await apiClient.get(url, { params });
    return response.data;
  }

  /**
   * GET /api/admin/sessions/list
   * @param {Object} params
   * @param {number} [params.page] - Page number (defaults to 1)
   * @param {number} [params.page_size] - Items per page (defaults to 20)
   * @param {string} [params.session_id] - Filter by session ID
   * @param {string} [params.user_id] - Filter by user ID
   * @param {string} [params.selected_date] - YYYY-MM-DD
   * @param {string} [params.start_time] - HH:MM:SS
   * @param {string} [params.end_time] - HH:MM:SS
   * @param {number} [params.days] - 1, 7, or 30
   * @returns {Promise<SessionsResponse>}
   */
  async getSessions(params = {}) {
    const url = getApiUrl(API_ENDPOINTS.ADMIN.SESSIONS.LIST);
    const response = await apiClient.get(url, { params });
    return response.data;
  }

  /**
   * GET /api/sessions/{session_id}/messages
   * Fetch messages for a session with visualization data
   * @param {string} sessionId
   * @returns {Promise<Array>}
   */
  async getSessionMessages(sessionId) {
    const baseUrl = getApiUrl("").replace(/\/api\/$/, "/api");
    const url = `${baseUrl}/sessions/${sessionId}/messages?include_deleted=true`;
    const response = await apiClient.get(url);
    return response.data;
  }

  /**
   * GET /api/admin/sessions/{session_id}
   * @param {string} sessionId
   * @returns {Promise<SessionConversationResponse>}
   */
  async getSessionConversation(sessionId) {
    const url = getApiUrl(API_ENDPOINTS.ADMIN.SESSIONS.DETAIL(sessionId));
    const response = await apiClient.get(url);
    return response.data;
  }

  /**
   * GET /api/admin/feedbacks
   * Mirrors the backend feedback analytics endpoint.
   * @param {Object} params
   * @param {number} [params.page]
   * @param {number} [params.page_size]
   * @param {string} [params.session_id]
   * @param {string} [params.user_id]
   * @returns {Promise<any>}
   */
  async getFeedbacks(params = {}) {
    const url = getApiUrl(API_ENDPOINTS.ADMIN.FEEDBACKS);
    try {
      const response = await apiClient.get(url, { params });
      const data = response.data;
      // New backend endpoint returns a plain array of feedback objects.
      // Normalize it into the paginated shape expected by the dashboard.
      if (Array.isArray(data)) {
        const page = params.page || 1;
        const pageSize = params.page_size || data.length || 10;
        return {
          total: data.length,
          page,
          page_size: pageSize,
          total_pages: 1,
          feedbacks: data,
        };
      }
      // If the backend already returns a structured object, pass it through.
      return data;
    } catch (error) {
      // If the backend has not yet implemented the feedback endpoint,
      // return an empty, but correctly shaped, response so that the
      // admin UI continues to work without breaking.
      if (error.response && error.response.status === 404) {
        return {
          total: 0,
          page: params.page || 1,
          page_size: params.page_size || 10,
          total_pages: 1,
          feedbacks: [],
        };
      }
      throw error;
    }
  }

  /**
   * GET /api/admin/feedback/list
   * Fetch detailed feedback data with pagination and time filtering
   * @param {Object} params
   * @param {number} [params.page]
   * @param {number} [params.page_size]
   * @param {string} [params.selected_date] - YYYY-MM-DD
   * @param {string} [params.start_time] - HH:MM:SS
   * @param {string} [params.end_time] - HH:MM:SS
   * @param {number} [params.days] - 1, 7, or 30
   * @param {string} [params.session_id]
   * @param {string} [params.user_id]
   * @param {number} [params.feedback] - -1, 0, or 1
   * @returns {Promise<any>}
   */
  async getFeedbacksDetailed(params = {}) {
    const url = getApiUrl(API_ENDPOINTS.ADMIN.FEEDBACKS_DETAILED);
    try {
      const response = await apiClient.get(url, { params });
      const data = response.data;
      
      // Handle array response
      if (Array.isArray(data)) {
        const page = params.page || 1;
        const pageSize = params.page_size || data.length || 20;
        return {
          total: data.length,
          page,
          page_size: pageSize,
          total_pages: Math.ceil(data.length / pageSize),
          feedbacks: data,
        };
      }
      
      // Handle structured response with 'feedback' key
      if (data && data.feedback && Array.isArray(data.feedback)) {
        return {
          total: data.total || data.feedback.length,
          page: data.page || params.page || 1,
          page_size: data.page_size || params.page_size || 20,
          total_pages: data.total_pages || Math.ceil((data.total || data.feedback.length) / (data.page_size || params.page_size || 20)),
          feedbacks: data.feedback,
        };
      }
      
      // Handle structured response with 'feedbacks' key
      if (data && data.feedbacks && Array.isArray(data.feedbacks)) {
        return {
          total: data.total || data.feedbacks.length,
          page: data.page || params.page || 1,
          page_size: data.page_size || params.page_size || 20,
          total_pages: data.total_pages || Math.ceil((data.total || data.feedbacks.length) / (data.page_size || params.page_size || 20)),
          feedbacks: data.feedbacks,
        };
      }
      
      // Fallback: return data as-is if it has the expected structure
      return data;
    } catch (error) {
      if (error.response && error.response.status === 404) {
        return {
          total: 0,
          page: params.page || 1,
          page_size: params.page_size || 20,
          total_pages: 1,
          feedbacks: [],
        };
      }
      throw error;
    }
  }
}

export const adminService = new AdminService();
export default adminService;


